// @flow
/* eslint-disable no-plusplus */
import {
  nAry,
  compose,
  keys,
  length,
  prepend,
  take,
  drop,
  curry,
  isEmpty,
  any,
  memoize,
  pipe,
  of,
  ap,
  apply
} from 'ramda';
import moment from 'moment';
import type { Reducer } from '../types';
import { isJp } from './location';

export const groupsOf = curry(function group(n, list: any) {
  return isEmpty(list) ? [] : prepend(take(n, list), group(n, drop(n, list)));
});

export const objSize = nAry(
  1,
  compose(
    length,
    keys
  )
);

/**
 * makeActionCreator is a boilerplate helper for redux,
 * which should be used when creating non-async actions.
 * usage: const actionCreator = makeActionCreator(CONSTANT_NAME, 'paramName1', 'paramName2');
 * @param type
 * @param argNames
 */
export const makeActionCreator = (type: string, ...argNames: Array<string>) => (
  ...args: Array<mixed>
): any => {
  const action = { type };
  argNames.forEach((arg, index) => {
    action[argNames[index]] = args[index];
  });
  return action;
};

/**
 * Reduce is a switch replacement/boilerplate helper for redux.
 * It helps with flow type consistency.
 * @param initialState
 * @param handlers
 * @returns {reducer}
 */
export function reduce<S, A: *>(
  initialState: S,
  handlers: { [key: string]: Reducer<S, A> }
): Reducer<S, A> {
  return function reducer(state: S = initialState, action: A): S {
    return Object.prototype.hasOwnProperty.call(handlers, action.type)
      ? handlers[action.type](state, action)
      : state;
  };
}

const bad = fns => any(fn => typeof fn !== 'function')(fns);
const fnsOrArray = fns => (Array.isArray(fns[0]) ? fns[0] : fns.slice(0, -1));

/**
 * createSelector is a selector helper like reselect.js - useful for computed properties from state
 * - it memoizes its args and their return so computation will only ever be performed once for
 * the same args and state
 * @param fns
 */
export const createSelector = (...fns: Array<Function>) => (
  state: any,
  props: any
) => {
  const preFns = fnsOrArray(fns).map(fn => stateAndProps =>
    fn(...stateAndProps)
  );

  if (bad(preFns)) {
    return () => {
      throw new Error('reselect: input-selectors must be a function');
    };
  }

  const lastFn = memoize(fns[fns.length - 1]);

  return pipe(
    of,
    ap(preFns),
    apply(lastFn)
  )([state, props]);
};

export const getQueryParam = (search: string, name: string) => {
  const match = RegExp(`[?&]${name}=([^&]*)`).exec(search);
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
};

export const getCookieValue = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
};

export const dateFromPair = (day: Date, time: string) => {
  const date = new Date(day);
  const [hours, minutes, seconds] = time
    .split(':')
    .map(str => parseInt(str, 10));
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  return date;
};

export const availabilityString = (availability: number) => {
  if (availability < 33) {
    return 'Unlikely';
  }
  if (availability < 66) {
    return 'Moderate';
  }
  if (availability < 95) {
    return 'Likely';
  }
  return "We're certain";
};

export const availabilityColor = (availability: number) => {
  if (availability >= 0.8) {
    return '#1fa637';
  }
  if (availability >= 0.5) {
    return '#fab03a';
  }
  return '#fa4949';
};

export const milesToMeters = (miles: number) => Math.round(miles * 1609);

const spreadKey = (category: string, radius: number) =>
  `${category}_${radius}m`;

export const resultSpread = (results: Array<any>) => {
  const spread = {};
  const categories = ['jp', 'aux', 'all'];
  const radii = [100, 250, 400, 550, 700];

  categories.forEach(cat => {
    radii.forEach(rad => {
      spread[spreadKey(cat, rad)] = 0;
    });

    spread[`${cat}_total`] = 0;
  });

  results.forEach(result => {
    const cat = isJp(result) ? 'jp' : 'aux';

    radii.forEach(radius => {
      if (result.distanceInMeters <= radius) {
        spread[spreadKey(cat, radius)]++;
        spread[spreadKey('all', radius)]++;
      }
    });

    spread[`${cat}_total`]++;
    spread.all_total++;
  });

  return spread;
};

/**
 * Return an array of numbers
 *
 * @param start
 * @param end
 * @param step
 * @returns {Array}
 */
export const range = (start, end, step = 1) => {
  const arr = [];

  for (let i = 0; i < end / step; i++) {
    arr.push(i * step);
  }

  return arr;
};

/**
 * Takes an array, current index, and a value to increment the index by
 * Calculates the index, once we reach the end of the array we loop back to the start
 *
 * @param array
 * @param index
 * @param increment
 * @returns {*}
 */
export const arrayCycle = (array, index, increment) => {
  const max = array.length;
  const newIndex = index + (increment % max);

  if (newIndex > max - 1) {
    return newIndex % max;
  }

  if (newIndex < 0) {
    return max + newIndex;
  }

  return newIndex;
};

export const roundDateToNearest = (date, step) =>
  date
    .clone()
    .startOf('minute')
    .add(step - (date.minute() % step), 'm');

/**
 * Checks if arriving on time is in the past during prebook
 * @param {Moment} arriving
 * @param {Moment} currentTime
 *
 * @returns {Moment} restricted arriveOn time
 */
export const restrictPastBooking = arriving => {
  const currentTime = moment();

  return arriving.isBefore(currentTime)
    ? arriving.set({
        year: currentTime.get('year'),
        month: currentTime.get('month'),
        date: currentTime.get('date'),
        hour: currentTime.get('hour'),
        minute: Math.floor(currentTime.get('minute') / 5) * 5
      })
    : arriving;
};

/**
 * Calculates the distance between two latLng values in metres
 * Code gotten from https://www.geodatasource.com/developers/javascript
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} Distance
 */
export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  if (lat1 === lat2 && lng1 === lng2) {
    return 0;
  }
  const radlat1 = (Math.PI * lat1) / 180;
  const radlat2 = (Math.PI * lat2) / 180;
  const theta = lng1 - lng2;
  const radtheta = (Math.PI * theta) / 180;
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  // convert to meters
  dist *= 1609.344;
  return dist;
};

export const getIdxByKey = (array: Array<any>, key: string, value: any) => {
  let indexOfTempItem = -1;
  array.find((item, idx) => {
    indexOfTempItem = idx;
    return item[key] === value;
  });
  return indexOfTempItem;
};

export const getIdxById = (array, id) => getIdxByKey(array, 'id', id);
export default {
  groupsOf,
  objSize,
  makeActionCreator,
  reduce,
  createSelector,
  getQueryParam,
  dateFromPair,
  availabilityString,
  range
};
